import React, { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { Link } from "react-router-dom";
import {
  MdMessage,
  MdSend,
  MdCheckCircle,
  MdError,
  MdCampaign,
  MdPeople,
  MdTrendingUp,
  MdArrowForward,
} from "react-icons/md";

// TODO:
// import {
//   getRCSDashboard,
//   getTodayRCSDashboard,
// } from "../../../redux/services/rcsDashboardServices/rcsDashboard.Services";

const RCSDashboard = () => {
  const [view, setView] = useState("overall");
  const [loading, setLoading] = useState(true);

  const [summary, setSummary] = useState({});
  const [todaySummary, setTodaySummary] = useState({});

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        setLoading(true);

        // const [overall, today] = await Promise.all([
        //   getRCSDashboard(),
        //   getTodayRCSDashboard(),
        // ]);

        // setSummary(overall?.data || {});
        // setTodaySummary(today?.data || {});

        setSummary({});
        setTodaySummary({});
      } catch (error) {
        console.error("RCS Dashboard Error:", error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, []);

  const overallTiles = useMemo(
    () => [
      {
        icon: <MdCampaign />,
        label: "Total Campaigns",
        href: "/rcs-campaign-list",
        points: summary?.campaigns || 0,
        accent: "cyan",
        helper: "All RCS campaigns",
      },
      {
        icon: <MdSend />,
        label: "Messages Sent",
        href: "/rcs-message-list",
        points: summary?.messages_sent || 0,
        accent: "blue",
        helper: "Total messages",
      },
      {
        icon: <MdCheckCircle />,
        label: "Delivered",
        href: "/rcs-message-list",
        points: summary?.delivered || 0,
        accent: "green",
        helper: "Delivered messages",
      },
      {
        icon: <MdError />,
        label: "Failed",
        href: "/rcs-message-list",
        points: summary?.failed || 0,
        accent: "red",
        helper: "Failed messages",
      },
      {
        icon: <MdPeople />,
        label: "Active Users",
        href: "/rcs-users",
        points: summary?.active_users || 0,
        accent: "purple",
        helper: "Active RCS users",
      },
      {
        icon: <MdTrendingUp />,
        label: "Delivery Rate",
        href: "/rcs-report",
        points: `${summary?.delivery_rate || 0}%`,
        accent: "orange",
        helper: "Message delivery",
      },
    ],
    [summary]
  );

  const todayTiles = useMemo(
    () => [
      {
        icon: <MdCampaign />,
        label: "Campaigns Today",
        href: "/rcs-campaign-list",
        points: todaySummary?.campaigns || 0,
        accent: "cyan",
        helper: "Today's campaigns",
      },
      {
        icon: <MdSend />,
        label: "Messages Today",
        href: "/rcs-message-list",
        points: todaySummary?.messages_sent || 0,
        accent: "blue",
        helper: "Today's messages",
      },
      {
        icon: <MdCheckCircle />,
        label: "Delivered Today",
        href: "/rcs-message-list",
        points: todaySummary?.delivered || 0,
        accent: "green",
        helper: "Today's delivery",
      },
      {
        icon: <MdError />,
        label: "Failed Today",
        href: "/rcs-message-list",
        points: todaySummary?.failed || 0,
        accent: "red",
        helper: "Today's failures",
      },
      {
        icon: <MdPeople />,
        label: "Active Users",
        href: "/rcs-users",
        points: todaySummary?.active_users || 0,
        accent: "purple",
        helper: "Today's active users",
      },
    ],
    [todaySummary]
  );

  const currentData = view === "overall" ? summary : todaySummary;
  const tiles = view === "overall" ? overallTiles : todayTiles;

  const deliveryChart = {
    series: [
      Number(currentData?.delivered || 0),
      Number(currentData?.failed || 0),
      Number(currentData?.pending || 0),
    ],
    options: {
      chart: {
        type: "donut",
        toolbar: { show: false },
      },
      labels: ["Delivered", "Failed", "Pending"],
      legend: {
        position: "bottom",
      },
    },
  };

  const messagePerformance = currentData?.message_performance || [];

  const performanceChart = {
    series: [
      {
        name: "Sent",
        data: messagePerformance.map((item) =>
          Number(item?.sent || 0)
        ),
      },
      {
        name: "Delivered",
        data: messagePerformance.map((item) =>
          Number(item?.delivered || 0)
        ),
      },
    ],
    options: {
      chart: {
        type: "bar",
        toolbar: { show: false },
      },
      xaxis: {
        categories: messagePerformance.map(
          (item) => item?.date || item?.campaign || ""
        ),
      },
      plotOptions: {
        bar: {
          borderRadius: 5,
          columnWidth: "45%",
        },
      },
      legend: {
        position: "bottom",
      },
    },
  };

  if (loading) {
    return (
      <div className="crm-dashboard crm-dashboard-loading">
        <div className="crm-loader-ring"></div>
      </div>
    );
  }

  return (
    <div className="crm-dashboard">
      <section className="crm-dashboard-hero">
        <div className="crm-hero-glow"></div>

        <div className="crm-hero-content">
          <div>
            <span className="crm-ai-badge">
              <MdMessage /> RCS
            </span>

            <h1>RCS Messaging Dashboard</h1>

            <p>
              Track RCS campaigns, rich messages, users and delivery
              performance.
            </p>

            <div className="crm-hero-meta">
              <span>
                <MdTrendingUp />
                RCS messaging analytics
              </span>
            </div>
          </div>

          <div className="crm-hero-stats">
            <div>
              <strong>{currentData?.messages_sent || 0}</strong>
              <span>Messages Sent</span>
            </div>

            <div>
              <strong>{currentData?.delivery_rate || 0}%</strong>
              <span>Delivery Rate</span>
            </div>
          </div>
        </div>
      </section>

      <div className="crm-section-head">
        <div>
          <span className="crm-eyebrow">RCS OVERVIEW</span>
          <h2>RCS Analytics</h2>
        </div>

        <div className="crm-view-switch">
          <button
            className={view === "overall" ? "active" : ""}
            onClick={() => setView("overall")}
          >
            Overall
          </button>

          <button
            className={view === "today" ? "active" : ""}
            onClick={() => setView("today")}
          >
            Today
          </button>
        </div>
      </div>

      <div className="crm-kpi-grid">
        {tiles.map((item, index) => (
          <Link
            to={item.href}
            className={`crm-kpi-card ${item.accent}`}
            key={index}
          >
            <div className="crm-kpi-top">
              <div className="crm-kpi-icon">{item.icon}</div>
              <MdArrowForward className="crm-kpi-arrow" />
            </div>

            <div className="crm-kpi-value">{item.points}</div>
            <div className="crm-kpi-label">{item.label}</div>
            <div className="crm-kpi-helper">{item.helper}</div>
            <div className="crm-kpi-line"></div>
          </Link>
        ))}
      </div>

      <div className="crm-chart-grid">
        <div className="crm-chart-card">
          <div className="crm-card-heading">
            <div>
              <span className="crm-live-dot"></span>
              Message Status
            </div>
            <span className="crm-period-chip">Live</span>
          </div>

          <div className="crm-chart-body">
            <Chart
              options={deliveryChart.options}
              series={deliveryChart.series}
              type="donut"
              height={320}
            />
          </div>
        </div>

        <div className="crm-chart-card">
          <div className="crm-card-heading">
            <div>
              <span className="crm-live-dot"></span>
              Message Performance
            </div>
            <span className="crm-period-chip">Analytics</span>
          </div>

          <div className="crm-chart-body">
            {messagePerformance.length ? (
              <Chart
                options={performanceChart.options}
                series={performanceChart.series}
                type="bar"
                height={320}
              />
            ) : (
              <div className="crm-empty-chart">
                No RCS performance data available
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RCSDashboard;